import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, ShoppingCart, ArrowLeft, Filter, Leaf, Drumstick, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import CartDrawer from '@/components/common/CartDrawer';
import MenuItemCard from '@/components/menu/MenuItemCard';
import FixedFooter from '@/components/common/FixedFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CafePlusPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getCartCount } = useCart();
  
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [vegFilter, setVegFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(searchParams.get('search') ? true : false);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterItems();
  }, [menuItems, selectedCategory, vegFilter, searchQuery]);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu/items?outlet=cafe-plus`);
      // Filter out unavailable items on frontend as well for extra safety
      const availableItems = response.data.filter(item => item.available !== false);
      setMenuItems(availableItems);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/menu/categories?outlet=cafe-plus`);
      const cafePlusCategories = response.data['cafe-plus'] || [];
      setCategories(cafePlusCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filterItems = () => {
    let filtered = [...menuItems];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    if (vegFilter !== 'all') {
      if (vegFilter === 'veg') {
        filtered = filtered.filter(item => item.veg === 'veg' || item.veg === 'both');
      } else if (vegFilter === 'non-veg') {
        filtered = filtered.filter(item => item.veg === 'non-veg' || item.veg === 'both');
      }
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const groupItemsByCategory = () => {
    const grouped = {};
    filteredItems.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    return grouped;
  };

  // Get categories in the order they appear in menu items
  const getOrderedCategories = () => {
    const categoryOrder = [];
    const seen = new Set();
    
    menuItems.forEach(item => {
      if (!seen.has(item.category)) {
        categoryOrder.push(item.category);
        seen.add(item.category);
      }
    });
    
    return categoryOrder;
  };

  const formatCategoryName = (category) => {
    // Handle special cases for better formatting
    const specialCases = {
      'round-the-clock-beverages': 'Round The Clock Beverages',
      'round-the-clock-drinks': 'Round The Clock Beverages', // Legacy support
      'sip-and-savor': 'Sip & Savor',
      'sip-savor': 'Sip & Savor' // Legacy support
    };
    
    if (specialCases[category]) {
      return specialCases[category];
    }
    
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--burgundy)' }}></div>
          <p style={{ color: 'var(--charcoal)' }}>Loading menu...</p>
        </div>
      </div>
    );
  }

  const groupedItems = groupItemsByCategory();

  return (
    <div className="min-h-screen pb-10">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-burgundy border-b" style={{ borderColor: 'rgba(224, 178, 59, 0.2)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-burgundy hover:opacity-80"
              style={{ color: 'var(--burgundy)' }}
              data-testid="back-to-home-button"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Home</span>
            </button>

            <img
              src="/images/2 25.png"
              alt="Café Plus"
              className="h-12"
            />

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-full glass hover:shadow-lg ${
                  showFilters ? 'bg-burgundy-light' : ''
                }`}
                style={{ color: 'var(--burgundy)' }}
                title="Toggle Filters"
              >
                <Filter size={20} />
              </button>
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 rounded-full glass hover:shadow-lg"
                style={{ color: 'var(--burgundy)' }}
                title="Toggle Search"
              >
                <Search size={20} />
              </button>
              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center gap-2 btn-gold"
                data-testid="cart-button"
              >
                <ShoppingCart size={20} />
                {getCartCount() > 0 && (
                  <span
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: 'var(--burgundy)' }}
                    data-testid="cart-count"
                  >
                    {getCartCount()}
                  </span>
                )}
                <span className="hidden sm:inline">Order</span>
              </button>
            </div>
          </div>

          {/* Search Bar - Toggleable */}
          {showSearch && (
            <div className="mt-4" key="search-bar">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2" size={20} style={{ color: 'var(--charcoal)' }} />
                <Input
                  type="text"
                  placeholder="Search dishes, beverages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 rounded-full glass"
                  data-testid="search-input"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="clear-search-button"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Filters - Toggleable */}
          {showFilters && (
            <div className="mt-4 flex items-center gap-3 overflow-x-auto pb-2" key="filters-bar">
              <div className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                <Filter size={16} />
                <span>Filters:</span>
              </div>

              {/* Veg Filter */}
              <button
                onClick={() => setVegFilter(vegFilter === 'veg' ? 'all' : 'veg')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                  vegFilter === 'veg' ? 'bg-green-100 text-green-700 border-2 border-green-500' : 'glass text-charcoal'
                }`}
                data-testid="veg-filter-button"
              >
                <Leaf size={16} />
                Veg
              </button>

              <button
                onClick={() => setVegFilter(vegFilter === 'non-veg' ? 'all' : 'non-veg')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                  vegFilter === 'non-veg' ? 'bg-red-100 text-red-700 border-2 border-red-500' : 'glass text-charcoal'
                }`}
                data-testid="non-veg-filter-button"
              >
                <Drumstick size={16} />
                Non-Veg
              </button>

              {/* Category Pills */}
              <div className="h-8 w-px bg-gray-300" />
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                  selectedCategory === 'all' ? 'btn-primary' : 'glass'
                }`}
                data-testid="category-all-button"
              >
                All
              </button>
              {getOrderedCategories().map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                    selectedCategory === category ? 'btn-primary' : 'glass'
                  }`}
                  data-testid={`category-${category}-button`}
                >
                  {formatCategoryName(category)}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Menu Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {Object.keys(groupedItems).length === 0 ? (
          <div className="text-center py-10">
            <p className="text-xl" style={{ color: 'var(--charcoal)' }}>No items found</p>
            <p className="text-sm mt-2" style={{ color: 'var(--charcoal)', opacity: 0.7 }}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          getOrderedCategories().map((category) => {
            const items = groupedItems[category];
            if (!items || items.length === 0) return null;
            
            return (
              <div key={category} className="mb-12">
                <h2 
                  className="text-2xl sm:text-3xl mb-4"
                  style={{ color: 'var(--burgundy)' }}
                  data-testid={`category-header-${category}`}
                >
                  {formatCategoryName(category)}
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {items.map((item) => (
                    <MenuItemCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8 px-4 glass-burgundy border-t" style={{ borderColor: 'rgba(224, 178, 59, 0.2)' }}>
        <div className="max-w-7xl mx-auto">
          {/* Dietary Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-6">
            <div className="flex items-center gap-2">
              <Leaf size={16} className="text-green-600" />
              <span className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>Vegetarian</span>
            </div>
            <div className="flex items-center gap-2">
              <Drumstick size={16} className="text-red-600" />
              <span className="text-sm font-medium" style={{ color: 'var(--charcoal)' }}>Non-Vegetarian</span>
            </div>
          </div>
          
          {/* Footer Text */}
          <div className="text-center space-y-3">
            <p className="text-sm" style={{ color: 'var(--charcoal)' }}>
              All prices are in Indian Rupees and are exclusive of applicable Government taxes. 
              We levy discretionary service charge of 5%. <span className="font-semibold">Payment of Service charge is voluntary.</span>
            </p>
            <p className="text-sm" style={{ color: 'var(--charcoal)' }}>
              For allergen information, please consult a member of our staff prior to ordering.
            </p>
          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Fixed Footer */}
      <FixedFooter />
    </div>
  );
};

export default CafePlusPage;